// src/components/common/KaliTaskOutputViewer.js
//
// Lazy-loaded streamed-stdout/stderr viewer for any kali_command BeaconTask.
// Originally inlined in AIPentest/Index.js; extracted here so AiTestCard on
// the Findings expansion (and future callers) can share the same component.
// Polls the orchestrator's task-output endpoint with a Show/Hide toggle and
// a Copy button. Auto-poll while the task is presumed running (stops on
// pause + on operator hide).

import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, Terminal } from 'lucide-react';
import { orchestratorAPI } from '../../services/api';

export default function KaliTaskOutputViewer({
  taskId,
  defaultOpen = false,
  pollMs = 4000,
  pollWhile = (lines) => lines.length === 0 || lines.length < 5000,
}) {
  const [shown, setShown] = useState(defaultOpen);
  const [lines, setLines] = useState([]);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  const fetchLines = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await orchestratorAPI.taskOutput(taskId, { afterSeq: 0, limit: 5000 });
      setLines(data?.lines || []);
      setTotal(data?.total || 0);
      setTruncated(!!data?.truncated);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  // Auto-poll while the panel is open and pollWhile() returns true. Stops
  // on hide. The default predicate keeps polling until the buffer fills
  // (5000 lines) - good enough for most kali_command runs.
  useEffect(() => {
    if (!shown) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (lines.length === 0) {
      fetchLines();
    }
    pollRef.current = setInterval(() => {
      if (pollWhile(lines)) fetchLines();
    }, pollMs);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, taskId]);

  const onToggle = () => setShown((v) => !v);

  const onCopy = async () => {
    const text = lines.map((l) => `${l.stream === 'stderr' ? 'ERR ' : '    '}${l.line}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API unavailable; ignore
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 hover:text-shinobi-green border border-cyber-300/40 hover:border-shinobi-green/40 rounded px-2 py-1 transition"
      >
        <Terminal className="w-3 h-3" />
        {shown ? 'Hide raw output' : 'Show raw output'}
        {total > 0 && <span className="text-slate-500 normal-case tracking-normal">({total} line{total === 1 ? '' : 's'})</span>}
      </button>
      {shown && (
        <div className="mt-2 rounded-lg border border-cyber-300/40 bg-cyber-100/60 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-cyber-300/40 bg-cyber-200/40">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {loading ? 'Loading' : truncated ? `Showing first ${lines.length} of ${total} lines (truncated)` : `${lines.length} line${lines.length === 1 ? '' : 's'}`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchLines}
                title="Refresh"
                className="text-slate-500 hover:text-slate-800"
                disabled={loading}
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={onCopy}
                disabled={lines.length === 0}
                className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-shinobi-green disabled:opacity-40"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          {err && <div className="px-3 py-2 text-[11px] text-red-300">{err}</div>}
          {!err && lines.length === 0 && !loading && (
            <div className="px-3 py-3 text-[11px] text-slate-500">No output captured yet. The beacon will stream lines here as the command produces them.</div>
          )}
          {!err && lines.length > 0 && (
            <pre className="text-[11px] font-mono text-slate-800 leading-snug px-3 py-2 overflow-auto max-h-96 whitespace-pre-wrap break-all">
              {lines.map((l) => (
                <div key={l.seq} className={l.stream === 'stderr' ? 'text-rose-300' : 'text-slate-800'}>
                  {l.line}
                </div>
              ))}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
